import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams, Outlet } from 'react-router-dom';
import { cn } from "@/lib/utils/cn";
import { SuspenseWrapper } from "@/ui-components/ui/suspense-wrapper";
import { Button } from "@/ui-components/ui/button";
import { LucideIcon, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/util-components/dropdown-menu";

// --- Context & Types ---

type PageLayoutContextType = {
  currentTab: string;
  handleTabChange: (tab: string) => void;
  viewMode: 'grid' | 'list' | null;
  basePath: string;
};

const PageLayoutContext = createContext<PageLayoutContextType | undefined>(undefined);

export const usePageLayout = () => {
  const context = useContext(PageLayoutContext);
  if (!context) {
    throw new Error('usePageLayout must be used within a PageLayout');
  }
  return context;
};

interface PageLayoutProps {
  /** Base path for the page, e.g. "/post-generator" */
  basePath: string;
  /** Logic to resolve the default tab if on root path. Return the tab name to redirect to. */
  resolveDefaultTab?: () => string | null;
  /** Static default tab if no dynamic logic needed */
  defaultTab?: string;
  children: ReactNode;
  /** Suspense type for the generic wrapper */
  suspenseType?: "blue" | "green";
  /** Optional class name override for the main container */
  className?: string;
}

/**
 * Main Page Layout Wrapper
 * Handles routing logic, tab state, and view mode persistence.
 */
export function PageLayout({ 
  basePath, 
  resolveDefaultTab, 
  defaultTab, 
  children,
  suspenseType = "blue",
  className
}: PageLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Helper: Extract current tab from URL relative to basePath
  // This assumes the tab is the first segment after basePath
  // e.g. /post-generator/templates -> templates
  const getCurrentTab = (): string => {
    // Normalize paths to remove trailing slashes for comparison
    const path = location.pathname.endsWith('/') ? location.pathname.slice(0, -1) : location.pathname;
    const base = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
    
    if (path === base) return ''; // Root
    
    // Check if path starts with base
    if (path.startsWith(base)) {
        const remaining = path.substring(base.length);
        // Remove leading slash
        const cleanRemaining = remaining.startsWith('/') ? remaining.substring(1) : remaining;
        // The tab is the first segment
        return cleanRemaining.split('/')[0];
    }
    
    // Fallback to simple split logic if path manipulation fails or simple structure assumed
    return path.split('/').pop() || '';
  };

  const currentTab = getCurrentTab();
  
  // Helper: Extract view mode
  const getViewMode = () => {
    const viewParam = searchParams.get('view');
    return (viewParam === 'grid' || viewParam === 'list') ? viewParam as 'grid' | 'list' : null;
  };

  const currentViewMode = getViewMode();

  // Redirect Logic
  useEffect(() => {
    // Check if we are exactly on the base path
    const path = location.pathname.endsWith('/') ? location.pathname.slice(0, -1) : location.pathname;
    const base = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;

    if (path === base) {
      let targetTab = defaultTab;
      if (resolveDefaultTab) {
        const resolved = resolveDefaultTab();
        if (resolved) targetTab = resolved;
      }

      if (targetTab) {
        navigate(targetTab, { replace: true });
      }
    }
  }, [location.pathname, basePath, defaultTab, resolveDefaultTab, navigate]);

  const handleTabChange = (tab: string) => {
    const viewModeParam = currentViewMode ? `?view=${currentViewMode}` : '';
    // Construct new path: basePath + / + tab
    // Handle potential double slashes
    const base = basePath.endsWith('/') ? basePath : `${basePath}/`;
    navigate(`${base}${tab}${viewModeParam}`);
  };

  return (
    <PageLayoutContext.Provider value={{ currentTab, handleTabChange, viewMode: currentViewMode, basePath }}>
      <div className={cn("h-full w-full flex flex-col bg-gray-50", className)}>
        <main className="flex-1 overflow-y-auto">
          <div className="min-h-full flex flex-col">
             {children}
             <SuspenseWrapper type={suspenseType}>
               <Outlet context={{ currentTab }} />
             </SuspenseWrapper>
          </div>
        </main>
      </div>
    </PageLayoutContext.Provider>
  );
}

// --- Header Components ---

interface PageHeaderProps extends React.HTMLAttributes<HTMLElement> {
  children: ReactNode;
  className?: string;
}

export function PageHeader({ children, className, ...props }: PageHeaderProps) {
  return (
    <header className={cn("bg-white border-b border-gray-100 flex-shrink-0 z-30 relative", className)} {...props}>
      <div className="px-4 sm:px-6">
        <div className="flex items-center justify-between gap-4 py-3 sm:py-4 max-w-7xl mx-auto w-full overflow-hidden">
          {children}
        </div>
      </div>
    </header>
  );
}

interface PageTitleProps {
  title: string;
  icon?: LucideIcon;
  /** Tailwind class for icon color, e.g. "text-blue-600" */
  iconColor?: string;
  /** Tailwind class for icon container background, e.g. "bg-blue-50" */
  iconBg?: string;
  /** Optional extra content next to title */
  children?: ReactNode;
}

export function PageTitle({ title, icon: Icon, iconColor, iconBg = "bg-gray-50", children }: PageTitleProps) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      {Icon && (
        <div
          className={cn(
            "flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-xl border flex-shrink-0",
            (() => {
              const m = iconColor || "";
              const map: Record<string, { bg: string; border: string }> = {
                "text-blue": { bg: "bg-blue-50", border: "border-blue-100" },
                "text-indigo": { bg: "bg-indigo-50", border: "border-indigo-100" },
                "text-green": { bg: "bg-green-50", border: "border-green-100" },
                "text-teal": { bg: "bg-teal-50", border: "border-teal-100" },
                "text-red": { bg: "bg-red-50", border: "border-red-100" },
                "text-purple": { bg: "bg-purple-50", border: "border-purple-100" },
                "text-orange": { bg: "bg-orange-50", border: "border-orange-100" },
                "text-amber": { bg: "bg-amber-50", border: "border-amber-100" },
                "text-sky": { bg: "bg-sky-50", border: "border-sky-100" },
                "text-lime": { bg: "bg-lime-50", border: "border-lime-100" },
                "text-emerald": { bg: "bg-emerald-50", border: "border-emerald-100" },
                "text-cyan": { bg: "bg-cyan-50", border: "border-cyan-100" },
                "text-gray": { bg: "bg-gray-50", border: "border-gray-100" },
                "text-slate": { bg: "bg-slate-50", border: "border-slate-100" },
                "text-neutral": { bg: "bg-neutral-50", border: "border-neutral-100" },
                "text-zinc": { bg: "bg-zinc-50", border: "border-zinc-100" },
              };
              const key = Object.keys(map).find((k) => m.includes(k));
              const derived = key ? map[key] : { bg: iconBg, border: "border-gray-100" };
              return cn(derived.bg, derived.border);
            })(),
            iconColor
          )}
        >
          <Icon className={cn("h-4 w-4 sm:h-5 sm:w-5", iconColor)} />
        </div>
      )}
      <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 truncate">{title}</h1>
      {children}
    </div>
  );
}

interface PageNavProps {
  children: ReactNode;
}

export function PageNav({ children }: PageNavProps) {
  const { currentTab, handleTabChange } = usePageLayout();

  // Extract items from children to build mobile dropdown
  const navItems = React.Children.toArray(children)
    .filter(React.isValidElement)
    .map((child: any) => ({
        value: child.props.value,
        label: child.props.label,
        icon: child.props.icon,
        show: child.props.show ?? true
    }))
    .filter(item => item.show);

  const activeItem = navItems.find(i => i.value === currentTab) || navItems[0];

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="hidden sm:flex items-center gap-2">
        {children}
      </nav>

      {/* Mobile Navigation Dropdown */}
      <div className="sm:hidden flex items-center">
         <DropdownMenu>
           <DropdownMenuTrigger asChild>
             <Button variant="ghost" size="sm" className="h-8 gap-1 px-2 font-medium">
               <span className="text-muted-foreground">/</span>
               <span className="truncate max-w-[120px]">{activeItem?.label || 'Menu'}</span>
               <ChevronDown className="h-4 w-4 opacity-50" />
             </Button>
           </DropdownMenuTrigger>
           <DropdownMenuContent align="end" className="w-[200px]">
             {navItems.map(item => (
               <DropdownMenuItem 
                 key={item.value} 
                 onClick={() => handleTabChange(item.value)}
                 className={cn("cursor-pointer", currentTab === item.value && "bg-accent font-medium")}
               >
                 {item.icon && <item.icon className="mr-2 h-4 w-4" />}
                 {item.label}
               </DropdownMenuItem>
             ))}
           </DropdownMenuContent>
         </DropdownMenu>
      </div>
    </>
  );
}

interface PageNavItemProps {
  value: string;
  label: string;
  icon?: LucideIcon;
  /** If provided, renders only if valid. If boolean false passed (e.g. from permission check), hides item. */
  show?: boolean;
}

export function PageNavItem({ value, label, icon: Icon, show = true }: PageNavItemProps) {
  const { currentTab, handleTabChange } = usePageLayout();

  if (!show) return null;

  const isActive = currentTab === value;

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => handleTabChange(value)}
      className={cn(
        "h-8 px-3 text-sm font-medium rounded-xl transition-colors",
        isActive
          ? "bg-gray-900 text-white hover:bg-gray-800 hover:text-white" 
          : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
      )}
    >
      {Icon && <Icon className="w-4 h-4 mr-2" />}
      {label}
    </Button>
  );
}
